import React, { useMemo } from 'react';
import { WorkOrderReturn } from '../../clients/rust-api-client';
import {
  canAttachToWorkOrderComment,
  canModerateViolationComments,
  canPostInternalComment,
  canPostWorkOrderComment,
  canViewWorkOrderComments,
} from '../../constants/UserTypes';
import {
  getUserIdFromToken,
  getUserTypeFromToken,
} from '../../utils/TokenUtils';
import { workOrderEvents } from '../../violation/comments/activityEvents';
import { ActivityEventRow } from '../../violation/comments/ActivityEventRow';
import CommentThread from '../../violation/comments/CommentThread';
import { useAuthorNames } from '../../violation/comments/useAuthorNames';
import { useLinkedCommentId } from '../../violation/comments/useLinkedCommentId';

/**
 * A work order's activity timeline, drawn like the violation's (#603):
 * the moments the record stores, interleaved with its two comment
 * threads (#616) through the same `CommentThread` a violation uses.
 *
 * The one difference from a violation is the shared thread's audience —
 * the board, never a homeowner — and `CommentThread` phrases that from
 * the subject it is given. Work orders keep no change history, so there
 * are still no "changed status" rows.
 */
export const WorkOrderActivity: React.FC<{ workOrder: WorkOrderReturn }> = ({
  workOrder,
}) => {
  const userType = getUserTypeFromToken();
  const events = useMemo(() => workOrderEvents(workOrder), [workOrder]);
  const linkedCommentId = useLinkedCommentId();

  if (!canViewWorkOrderComments(userType)) {
    return <WorkOrderEventsOnly events={events} />;
  }

  return (
    <CommentThread
      subject={{ kind: 'work_order', id: workOrder.id }}
      currentUserId={getUserIdFromToken()}
      canComment={canPostWorkOrderComment(userType)}
      canPostInternal={canPostInternalComment(userType)}
      canModerate={canModerateViolationComments(userType)}
      canAttach={canAttachToWorkOrderComment(userType)}
      events={events}
      highlightCommentId={linkedCommentId}
    />
  );
};

/** The timeline without comments, for a caller the thread is not for. */
const WorkOrderEventsOnly: React.FC<{
  events: ReturnType<typeof workOrderEvents>;
}> = ({ events }) => {
  const actors = useMemo(
    () =>
      events
        .filter((e) => e.actorId)
        .map((e) => ({ author_id: e.actorId!, author_realm: 'user' as const })),
    [events]
  );
  const actorName = useAuthorNames(actors);

  return (
    <section aria-labelledby="work-order-activity">
      <h2
        id="work-order-activity"
        className="mb-3 text-[15px] font-semibold text-fg"
      >
        Activity
      </h2>
      {events.length === 0 ? (
        <p className="text-[13px] text-fg-muted">Nothing recorded yet.</p>
      ) : (
        <ol className="relative space-y-4 before:absolute before:bottom-3 before:left-[11px] before:top-3 before:w-px before:bg-line">
          {events.map((event) => (
            <ActivityEventRow
              key={event.id}
              event={event}
              actorName={(id) =>
                actorName({ author_id: id, author_realm: 'user' })
              }
            />
          ))}
        </ol>
      )}
    </section>
  );
};

export default WorkOrderActivity;
